import { useMemo } from 'react';

import type {
  CodexRun,
  ConversationMessage,
  MessageStatus,
  QueueActivityEvent,
  QueueItem,
  RunActivityTree,
  RunEvent
} from '../types';
import { sortConversationMessagesForDisplay } from '../utils/conversationThread';
import {
  buildRunActivityTreeFromEvents,
  compareConversationMessages,
  parseAgentSummaryUpdate,
  parseRunnerCommandUpdate,
  parseRunnerOutputLine,
  summarizeRunnerUpdate,
  totalActivityCommandCount,
  type RunnerState,
  type RunnerStep
} from '../utils/conversationRunActivity';
import {
  resolveTurnStatus,
  shouldQueueComposerSubmissions
} from '../utils/turnStatus';

const AUTOPILOT_MESSAGE_PRIORITY_MIN = 9;

export type LivePreview = {
  thinking?: string;
  command?: string;
  label?: string;
  text?: string;
};

type Args = {
  messages: ConversationMessage[];
  queue: QueueItem[];
  runs: CodexRun[];
  runEvents: RunEvent[];
  selectedActivityMessageId: string | null;
  queueActivityByMessageId: Record<string, QueueActivityEvent[]>;
  loadingOlderActivityByMessageId: Record<string, boolean>;
  queueActivityLoadingByMessageId: Record<string, boolean>;
  activityTreeByMessageId: Record<string, RunActivityTree>;
  holdQueuedMessages: boolean;
  conversationStatus: 'idle' | 'queued' | 'processing' | null | undefined;
  formatLogPayload: (event: RunEvent) => string;
  runnerPhaseProgress: Record<string, [number, number]>;
};

function groupWaitingQueueEvents(events: QueueActivityEvent[]): QueueActivityEvent[] {
  const grouped: QueueActivityEvent[] = [];
  for (const event of events) {
    if (event.event_name !== 'queue.waiting') {
      grouped.push(event);
      continue;
    }
    const prev = grouped[grouped.length - 1];
    if (!prev || prev.event_name !== 'queue.waiting') {
      grouped.push(event);
      continue;
    }
    const prevSignature = JSON.stringify({
      message: prev.message ?? null,
      count: prev.waiting_for_count ?? null,
      blockers: prev.blockers?.map((value) => `${value.conversation_id}:${value.message_id}`) ?? null
    });
    const nextSignature = JSON.stringify({
      message: event.message ?? null,
      count: event.waiting_for_count ?? null,
      blockers: event.blockers?.map((value) => `${value.conversation_id}:${value.message_id}`) ?? null
    });
    if (prevSignature === nextSignature) {
      grouped[grouped.length - 1] = event;
    } else {
      grouped.push(event);
    }
  }
  return grouped;
}

function isPreparationActivityEvent(event: QueueActivityEvent): boolean {
  return event.event_name === 'prepare.progress' || event.phase === 'preparation';
}

function buildRunnerState(
  runEvents: RunEvent[],
  hasActiveRun: boolean,
  formatLogPayload: (event: RunEvent) => string,
  runnerPhaseProgress: Record<string, [number, number]>
): RunnerState {
  const steps: RunnerStep[] = [];
  const stepById = new Map<string, RunnerStep>();
  let activeStepId: string | null = null;
  let latestMessage = '';
  let latestPhase: string | null = null;

  for (const event of runEvents) {
    if (event.stream === 'update') {
      const parsed = parseRunnerCommandUpdate(formatLogPayload(event));
      if (!parsed) {
        continue;
      }
      if (parsed.kind === 'command') {
        const phaseLabel = parsed.phase ?? '';
        const stepLabel = parsed.step ?? 'command';
        const commandId = parsed.commandId ?? `event-${event.id}`;
        let step = stepById.get(commandId);
        if (!step) {
          step = {
            id: commandId,
            phase: phaseLabel,
            step: stepLabel,
            status: parsed.status,
            command: parsed.command || undefined,
            exitCode: parsed.exitCode ?? null,
            outputs: [],
            startedAt: event.created_at,
            finishedAt: null
          };
          stepById.set(commandId, step);
          steps.push(step);
        } else {
          step.phase = phaseLabel;
          step.step = stepLabel;
          step.command = parsed.command || step.command;
          step.exitCode = parsed.exitCode ?? step.exitCode ?? null;
          step.status = parsed.status;
        }
        if (parsed.event === 'started') {
          activeStepId = commandId;
          step.startedAt = event.created_at;
        } else {
          step.finishedAt = event.created_at;
          if (activeStepId === commandId) {
            activeStepId = null;
          }
        }
        latestPhase = phaseLabel || latestPhase;
        const summary = summarizeRunnerUpdate(parsed);
        if (summary) {
          latestMessage = summary;
        }
      } else {
        if (parsed.kind === 'status') {
          latestPhase = parsed.phase ?? latestPhase;
        }
        const summary = summarizeRunnerUpdate(parsed);
        if (summary) {
          latestMessage = summary;
        }
      }
      continue;
    }

    if (event.stream === 'stdout' || event.stream === 'stderr') {
      const { commandId, step, text } = parseRunnerOutputLine(formatLogPayload(event));
      let targetStep: RunnerStep | undefined;
      if (commandId) {
        targetStep = stepById.get(commandId);
      }
      if (!targetStep && step) {
        targetStep = steps.find((existing) => existing.step === step);
      }
      if (!targetStep && activeStepId) {
        targetStep = stepById.get(activeStepId);
      }
      if (!targetStep && step) {
        targetStep = {
          id: `output-${event.id}`,
          phase: latestPhase ?? 'run',
          step,
          status: 'running',
          outputs: [],
          startedAt: event.created_at,
          finishedAt: null
        };
        stepById.set(targetStep.id, targetStep);
        steps.push(targetStep);
      }
      if (targetStep) {
        targetStep.outputs.push({ id: event.id, stream: event.stream, text });
      }
    }
  }

  const phaseKey = latestPhase?.toLowerCase() ?? null;
  let progress: number | null = null;
  if (phaseKey && runnerPhaseProgress[phaseKey]) {
    const [min, max] = runnerPhaseProgress[phaseKey];
    const phaseSteps = steps.filter((step) => step.phase.toLowerCase() === phaseKey);
    const completed = phaseSteps.filter((step) => step.status !== 'running').length;
    const fraction = phaseSteps.length ? completed / phaseSteps.length : 0;
    progress = Math.min(max, Math.max(min, min + (max - min) * fraction));
  }

  if (!latestMessage && hasActiveRun) {
    latestMessage = 'Agent is running';
  }

  return {
    message: latestMessage,
    phase: latestPhase,
    progress,
    steps,
    activeStepId
  };
}

export function buildRunByMessageIdMap(runs: CodexRun[]): Map<string, CodexRun> {
  const map = new Map<string, CodexRun>();
  for (const run of runs) {
    if (!run.message_id) {
      continue;
    }
    const existing = map.get(run.message_id);
    if (
      !existing ||
      (existing.status !== 'running' && run.status === 'running') ||
      new Date(run.created_at).getTime() > new Date(existing.created_at).getTime()
    ) {
      map.set(run.message_id, run);
    }
  }
  return map;
}

export function resolveStreamingRun(
  selectedActivityMessageId: string | null,
  runByMessageId: Map<string, CodexRun>,
  activeRun: CodexRun | null
): CodexRun | null {
  if (selectedActivityMessageId) {
    const selectedRun = runByMessageId.get(selectedActivityMessageId);
    if (selectedRun?.status === 'running') {
      return selectedRun;
    }
  }
  return activeRun?.status === 'running' ? activeRun : null;
}

export function useConversationDerivedState({
  messages,
  queue,
  runs,
  runEvents,
  selectedActivityMessageId,
  queueActivityByMessageId,
  loadingOlderActivityByMessageId,
  queueActivityLoadingByMessageId,
  activityTreeByMessageId,
  holdQueuedMessages,
  conversationStatus,
  formatLogPayload,
  runnerPhaseProgress
}: Args) {
  const activeRun = useMemo(() => {
    return runs.find((run) => run.status === 'running') ?? runs[0] ?? null;
  }, [runs]);

  const runByMessageId = useMemo(() => buildRunByMessageIdMap(runs), [runs]);

  const streamingRun = useMemo(
    () => resolveStreamingRun(selectedActivityMessageId, runByMessageId, activeRun),
    [selectedActivityMessageId, runByMessageId, activeRun]
  );

  const hasInteractiveApproval = Boolean(streamingRun?.approval_fifo);
  const hasActiveRun = streamingRun?.status === 'running';

  const queueByMessageId = useMemo(() => {
    const map = new Map<string, QueueItem>();
    for (const item of queue) {
      const existing = map.get(item.message_id);
      if (
        !existing ||
        new Date(item.updated_at).getTime() > new Date(existing.updated_at).getTime()
      ) {
        map.set(item.message_id, item);
      }
    }
    return map;
  }, [queue]);

  const autopilotMessageIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of queue) {
      if ((item.priority ?? 0) >= AUTOPILOT_MESSAGE_PRIORITY_MIN) {
        ids.add(item.message_id);
      }
    }
    return ids;
  }, [queue]);

  const orderedQueue = useMemo(() => {
    const active = queue.filter((item) => item.status === 'queued');
    return [...active].sort((left, right) => {
      if (left.priority !== right.priority) {
        return right.priority - left.priority;
      }
      return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
    });
  }, [queue]);

  const queueOrderByMessageId = useMemo(() => {
    const map = new Map<string, { index: number; total: number }>();
    orderedQueue.forEach((item, index) => {
      map.set(item.message_id, { index, total: orderedQueue.length });
    });
    return map;
  }, [orderedQueue]);

  const orderedQueuedMessageIds = useMemo(
    () => orderedQueue.map((item) => item.message_id),
    [orderedQueue]
  );

  const hasPendingQueue = useMemo(() => {
    const queuePending = queue.some(
      (item) => !['done', 'error', 'canceled'].includes(item.status)
    );
    const runPending = runs.some((run) => run.status === 'running');
    const latestUserMessagePending = [...messages]
      .reverse()
      .find((message) => message.role === 'user' && message.status === 'queued');
    return queuePending || runPending || Boolean(latestUserMessagePending);
  }, [queue, runs, messages]);

  const runnerState = useMemo(
    () => buildRunnerState(runEvents, hasActiveRun, formatLogPayload, runnerPhaseProgress),
    [runEvents, hasActiveRun, formatLogPayload, runnerPhaseProgress]
  );

  const assistantText = useMemo(() => {
    const chunks = runEvents
      .filter((event) => event.stream === 'assistant')
      .map((event) => event.payload);
    return chunks.join('');
  }, [runEvents]);

  const { assistantByUserId, assignedAssistantIds } = useMemo(() => {
    const map = new Map<string, ConversationMessage>();
    const assigned = new Set<string>();
    const assistantMessages = messages
      .filter((message) => message.role === 'assistant')
      .sort(compareConversationMessages);

    for (const assistantMessage of assistantMessages) {
      const parentMessageId = assistantMessage.parent_message_id ?? null;
      if (!parentMessageId) {
        continue;
      }
      const previous = map.get(parentMessageId);
      if (previous) {
        assigned.delete(previous.id);
      }
      map.set(parentMessageId, assistantMessage);
      assigned.add(assistantMessage.id);
    }

    return { assistantByUserId: map, assignedAssistantIds: assigned };
  }, [messages]);

  const displayMessages = useMemo(
    () => sortConversationMessagesForDisplay(messages, orderedQueuedMessageIds),
    [messages, orderedQueuedMessageIds]
  );

  const messagePreviewById = useMemo(() => {
    const byId = new Map<string, string>();
    for (const message of messages) {
      const compact = message.content.replace(/\s+/g, ' ').trim();
      const prefix = message.role === 'assistant' ? 'Assistant' : 'You';
      const preview = compact ? compact.slice(0, 72) : `${prefix} message`;
      byId.set(message.id, `${prefix}: ${preview}`);
    }
    return byId;
  }, [messages]);

  const latestUserMessage = useMemo(() => {
    for (let index = displayMessages.length - 1; index >= 0; index -= 1) {
      if (displayMessages[index].role === 'user') {
        return displayMessages[index];
      }
    }
    return null;
  }, [displayMessages]);

  const latestTurnStatus = latestUserMessage
    ? resolveTurnStatus({
        messageStatus: latestUserMessage.status as MessageStatus,
        queueStatus: queueByMessageId.get(latestUserMessage.id)?.status ?? null,
        runStatus: runByMessageId.get(latestUserMessage.id)?.status ?? null,
        assistantStatus: assistantByUserId.get(latestUserMessage.id)?.status ?? null
      })
    : null;

  const effectiveConversationStatus =
    latestTurnStatus === 'queued'
      ? 'queued'
      : latestTurnStatus === 'processing'
        ? 'processing'
        : latestUserMessage
          ? 'idle'
          : (conversationStatus ?? 'idle');

  const composerWillQueue = shouldQueueComposerSubmissions({
    holdQueuedMessages,
    effectiveConversationStatus,
    hasActiveRun,
    hasPendingQueue
  });

  const activeOutputEvents = useMemo(
    () => runEvents.filter((event) => event.stream === 'stdout' || event.stream === 'stderr'),
    [runEvents]
  );

  const activeUpdateEvents = useMemo(
    () => runEvents.filter((event) => event.stream === 'update'),
    [runEvents]
  );

  const activeRunActivityTree = useMemo(
    () => buildRunActivityTreeFromEvents(streamingRun, runEvents, 24),
    [streamingRun, runEvents]
  );

  const effectiveSelectedActivityMessageId =
    selectedActivityMessageId ?? streamingRun?.message_id ?? activeRun?.message_id ?? null;

  const selectedActivityRun = useMemo(() => {
    if (!effectiveSelectedActivityMessageId) {
      return null;
    }
    return runByMessageId.get(effectiveSelectedActivityMessageId) ?? null;
  }, [effectiveSelectedActivityMessageId, runByMessageId]);

  const selectedActivityQueueItem = useMemo(() => {
    if (!effectiveSelectedActivityMessageId) {
      return null;
    }
    return queueByMessageId.get(effectiveSelectedActivityMessageId) ?? null;
  }, [effectiveSelectedActivityMessageId, queueByMessageId]);

  const selectedQueueActivityEvents = useMemo(() => {
    if (!effectiveSelectedActivityMessageId) {
      return [];
    }
    return queueActivityByMessageId[effectiveSelectedActivityMessageId] ?? [];
  }, [effectiveSelectedActivityMessageId, queueActivityByMessageId]);

  const selectedQueueActivityGrouped = useMemo(
    () => groupWaitingQueueEvents(selectedQueueActivityEvents.filter((event) => !isPreparationActivityEvent(event))),
    [selectedQueueActivityEvents]
  );

  const selectedPreparationActivityGrouped = useMemo(
    () => selectedQueueActivityEvents.filter(isPreparationActivityEvent),
    [selectedQueueActivityEvents]
  );

  const selectedActivityTree = useMemo(() => {
    if (!effectiveSelectedActivityMessageId) {
      return null;
    }
    if (
      streamingRun?.status === 'running' &&
      streamingRun.message_id === effectiveSelectedActivityMessageId &&
      activeRunActivityTree
    ) {
      return activeRunActivityTree;
    }
    return activityTreeByMessageId[effectiveSelectedActivityMessageId] ?? null;
  }, [
    effectiveSelectedActivityMessageId,
    streamingRun,
    activeRunActivityTree,
    activityTreeByMessageId
  ]);

  const selectedActivityCommandCount = useMemo(
    () => totalActivityCommandCount(selectedActivityTree),
    [selectedActivityTree]
  );

  const selectedActivityLoadingOlder = Boolean(
    effectiveSelectedActivityMessageId &&
      loadingOlderActivityByMessageId[effectiveSelectedActivityMessageId]
  );

  const selectedActivityRunId = selectedActivityRun?.id ?? selectedActivityTree?.run_id ?? null;

  const selectedActivityQueueLoading = Boolean(
    effectiveSelectedActivityMessageId &&
      queueActivityLoadingByMessageId[effectiveSelectedActivityMessageId]
  );

  const activeLivePreview = useMemo<LivePreview | null>(() => {
    if (!hasActiveRun) {
      return null;
    }
    let latestThinking: string | undefined;
    let latestCommand: string | undefined;
    for (let index = runEvents.length - 1; index >= 0; index -= 1) {
      const event = runEvents[index];
      if (event.stream !== 'update') {
        continue;
      }
      const normalized = formatLogPayload(event).trim();
      if (!normalized) {
        continue;
      }
      const summary = parseAgentSummaryUpdate(normalized, event.id, event.created_at);
      if (summary) {
        if (!latestThinking && summary.stageId === 'thinking') {
          const text = summary.text.trim();
          if (text) {
            latestThinking = text;
          }
        }
        if (!latestCommand && summary.stageId === 'actions') {
          const text = summary.text.trim();
          if (text) {
            latestCommand = text;
          }
        }
      }
      if (latestThinking && latestCommand) {
        break;
      }
    }
    if (!latestThinking && !latestCommand) {
      return null;
    }
    return {
      thinking: latestThinking,
      command: latestCommand,
      label: latestCommand ? 'Latest action' : latestThinking ? 'Thinking' : undefined,
      text: latestCommand ?? latestThinking
    };
  }, [hasActiveRun, runEvents, formatLogPayload]);

  return {
    activeRun,
    runByMessageId,
    streamingRun,
    hasInteractiveApproval,
    hasActiveRun,
    queueByMessageId,
    autopilotMessageIds,
    orderedQueue,
    queueOrderByMessageId,
    orderedQueuedMessageIds,
    hasPendingQueue,
    runnerState,
    assistantText,
    assistantByUserId,
    assignedAssistantIds,
    displayMessages,
    messagePreviewById,
    latestUserMessage,
    latestTurnStatus,
    effectiveConversationStatus,
    composerWillQueue,
    activeOutputEvents,
    activeUpdateEvents,
    activeRunActivityTree,
    effectiveSelectedActivityMessageId,
    selectedActivityRun,
    selectedActivityQueueItem,
    selectedQueueActivityEvents,
    selectedPreparationActivityGrouped,
    selectedQueueActivityGrouped,
    selectedActivityTree,
    selectedActivityCommandCount,
    selectedActivityLoadingOlder,
    selectedActivityRunId,
    selectedActivityQueueLoading,
    activeLivePreview
  };
}
